import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  MANAGE_ACCESS_FEATURE_KEY,
  ManageUserAccessState,
} from './manage-user-access.reducer';

import {
  FILTER_GROUP,
  MAXIMUM_TINS_PER_USER,
  SELECTED_USER_TYPE,
} from '../manage-access.constant';

import { TinAccess, User, UserTinLob } from '../manage-access.model';
import { LOB_BEHAVIORAL, LOB_MEDBH, LOB_MEDICAL } from '@cigna/chcp/auth/util';

function matchesNameOrTinNumber(text: string, user: User): boolean {
  const search = text.toLowerCase();
  const last = user.lastName.toLowerCase();
  const first = user.firstName.toLowerCase();
  const tinMatch = user.tins
    .map((tin: TinAccess) => tin.tin)
    .find((tinNumber: string) => tinNumber.includes(search));
  return (
    last.includes(search) || first.includes(search) || tinMatch !== undefined
  );
}

// Lookup the 'Login' feature state managed by NgRx
export const getManageUserAccessState =
  createFeatureSelector<ManageUserAccessState>(MANAGE_ACCESS_FEATURE_KEY);

const getAllUsers = createSelector(
  getManageUserAccessState,
  (state) => state.users,
);

const getJobRoles = createSelector(
  getManageUserAccessState,
  (state) => state.jobRoles,
);

const getEntitlements = createSelector(
  getManageUserAccessState,
  (state) => state.entitlements,
);

const getAllTins = createSelector(
  getManageUserAccessState,
  (state) => state.allTins,
);

const getPendingUsers = createSelector(getManageUserAccessState, (state) =>
  state.users.filter((user) => user.hasTinsAwaitingApproval),
);

const isSubmitting = createSelector(
  getManageUserAccessState,
  (state) => state.submitting,
);

const getSubmitError = createSelector(
  getManageUserAccessState,
  (state) => state.submitError,
);

const isVendorSuccess = createSelector(
  getManageUserAccessState,
  (state) => state.isVendorSuccess,
);

const getUserTableView = createSelector(
  getManageUserAccessState,
  (state) => state.userTableView,
);

const getViewAccessView = createSelector(
  getManageUserAccessState,
  (state) => state.viewAccessView,
);

const getRemoveAccessView = createSelector(
  getManageUserAccessState,
  (state) => state.removeAccessView,
);

const getResetPasswordView = createSelector(
  getManageUserAccessState,
  (state) => state.resetPasswordView,
);

const getResetPasswordResponse = createSelector(
  getManageUserAccessState,
  (state) => state.resetPasswordResponse,
);

const getAssignmentMethod = createSelector(
  getManageUserAccessState,
  (state) => state.assignmentMethod,
);

const getPageOverviewFilterGroup = createSelector(
  getManageUserAccessState,
  (state) => state.pageOverviewFilterGroup,
);

const getPageOverviewFilterText = createSelector(
  getManageUserAccessState,
  (state) => state.pageOverviewFilterText,
);

const getPageOverviewUsersGrouped = createSelector(
  getManageUserAccessState,
  (state) =>
    state.pageOverviewFilterGroup === FILTER_GROUP.all
      ? state.users
      : state.users.filter((user) => user.hasTinsAwaitingApproval),
);

const getPageOverviewUsers = createSelector(
  getPageOverviewUsersGrouped,
  getPageOverviewFilterText,
  (users, filterText) =>
    users.filter((u) => matchesNameOrTinNumber(filterText, u)),
);

const getSelectedUsersToEdit = createSelector(
  getManageUserAccessState,
  (state) => state.selectedUsers,
);

const getSelectedUserType = createSelector(
  getManageUserAccessState,
  (state) => state.selectedUserType,
);

const getHiddenSteps = createSelector(getSelectedUserType, (type) =>
  type === 'new' ? [] : [0],
);

const getViewingUser = createSelector(
  getManageUserAccessState,
  (state) => state.viewingUser,
);

const getSelectedUsersLength = createSelector(
  getManageUserAccessState,
  (state) => state.selectedUsers.length,
);

const getSelectedUsersEntitlementsLoaded = createSelector(
  getSelectedUsersToEdit,
  getSelectedUserType,
  (users, type) =>
    type === 'new' ||
    (users && users[0] && users[0].tins && users[0].tins[0].entitlements),
);

const getSelectedPrintEmailUsers = createSelector(
  getManageUserAccessState,
  (state) => state.selectedEmailPrintUsers,
);

const getIndividualSelectedEntitlementsValid = createSelector(
  getSelectedUsersToEdit,
  getSelectedUserType,
  (selectedUsers, userType) =>
    userType === SELECTED_USER_TYPE.exisiting ||
    (userType === SELECTED_USER_TYPE.new &&
      selectedUsers.every((user) =>
        user.accessAfter.some((tin) =>
          tin.entitlements ? tin.entitlements.length > 0 : false,
        ),
      )),
);

const getBulkEditSelectedTins = createSelector(
  getManageUserAccessState,
  (state) => state.bulkEditSelectedTins,
);

const getBulkEditSelectedTinsValid = createSelector(
  getBulkEditSelectedTins,
  (tins) => tins && tins.length > 0 && tins.length <= MAXIMUM_TINS_PER_USER,
);

const getBulkEditSelectedEntitlements = createSelector(
  getManageUserAccessState,
  (state) => state.bulkEditSelectedEntitlements,
);

const getBulkEditSelectedEntitlementsValid = createSelector(
  getBulkEditSelectedEntitlements,
  getSelectedUserType,
  (entitlements, userType) =>
    userType === SELECTED_USER_TYPE.exisiting ||
    (userType === SELECTED_USER_TYPE.new && entitlements.length > 0),
);

const getUsersOverTinLimit = createSelector(
  getManageUserAccessState,
  (state) => state.usersOverTinLimit,
);

const getUsersOverDelegateLimit = createSelector(
  getManageUserAccessState,
  (state) => state.usersOverDelegateLimit,
);

const allTinsLoaded = createSelector(
  getManageUserAccessState,
  (state) => state.allTinsLoaded,
);

const updateUserTinSuccess = createSelector(
  getManageUserAccessState,
  (state) => state.updateUserTinSuccess,
);
const userTinLobLoading = createSelector(
  getManageUserAccessState,
  (state) => state.userTinLobLoading,
);

const userTinsWithLob = createSelector(
  getManageUserAccessState,
  (state) => state.userTinsWithLob,
);

const preferredLobOptions = createSelector(
  userTinsWithLob,
  (tins: UserTinLob[]) => {
    const hasMedbhTin = tins.some((tin) => tin.lob === LOB_MEDBH);
    const hasMedicalTin =
      tins.length && tins.some((tin) => tin.lob === LOB_MEDICAL);
    const hasBehavioralTin =
      tins.length && tins.some((tin) => tin.lob === LOB_BEHAVIORAL);
    return hasMedbhTin
      ? ['BOTH', 'CHCP', 'EBH']
      : hasMedicalTin && hasBehavioralTin
      ? ['BOTH']
      : hasMedicalTin
      ? ['CHCP']
      : hasBehavioralTin
      ? ['EBH']
      : [];
  },
);

export const manageAccessQuery = {
  getAllUsers,
  getJobRoles,
  getEntitlements,
  getAllTins,
  getPageOverviewUsers,
  getPendingUsers,
  isSubmitting,
  getSubmitError,
  isVendorSuccess,
  getUserTableView,
  getViewAccessView,
  getRemoveAccessView,
  getResetPasswordView,
  getResetPasswordResponse,
  getAssignmentMethod,
  getHiddenSteps,
  getPageOverviewFilterGroup,
  getPageOverviewFilterText,
  getSelectedUsersToEdit,
  getSelectedUserType,
  getViewingUser,
  getSelectedUsersLength,
  getSelectedUsersEntitlementsLoaded,
  getSelectedPrintEmailUsers,
  getBulkEditSelectedTins,
  getBulkEditSelectedTinsValid,
  getBulkEditSelectedEntitlements,
  getBulkEditSelectedEntitlementsValid,
  getIndividualSelectedEntitlementsValid,
  getUsersOverTinLimit,
  getUsersOverDelegateLimit,
  allTinsLoaded,
  userTinLobLoading,
  updateUserTinSuccess,
  userTinsWithLob,
  preferredLobOptions,
};
